import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import PasswordStrength from '../PasswordStrength';
import GeneratorQRDisplay from './GeneratorQRDisplay';
import adminAPI from '../../API/adminAPI';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import styles from './AuthComponent.module.css';

export default function AuthComponent(props) {

    const {
        mode,
        toggleModeRedirect,
        email,
        setEmail,
        password,
        setPassword,
        handleLogin,
        loginError,
        setLoginError,
        handleSignUp,
        gen_id,
        signupState,
        setSignupState,
        setUp,
        inviteData,
        inviteError
    } = props;

    const [ inputType, setInputType ] = useState('password');
    const [ emailValid, setEmailValid ] = useState(false);
    const [ message, setMessage ] = useState('');
    const [ loading, setLoading ] = useState(false);

    // handle inputs (shared by login/signup)
    const handleInputChange = (setter) => (e) => {
        const value = e.target.value;
        setter(value);

        switch (mode) {
            case 'login':
                if (loginError) setLoginError(false);
                break;
            case 'signup':
                if (message?.length > 0) setMessage('');
                break;
            default:
                break;
        }
    };

    // signup logic
    const checkEmailAvailability = useCallback(
        debounce(async (search) => {
            try {
                const res = await adminAPI.getUserByEmail(search);
                if (res.data) {
                    setEmailValid(false);
                    setMessage('unavailable');
                } else {
                    setEmailValid(true);
                    setMessage('');
                }
            } catch (err) {
                console.error('Error checking email:', err);
            }
        },500),
    []);

    // effect for email validation (runs only in signup)
    useEffect(() => {
        if (mode !== 'signup') {
            setEmailValid(false);
            setMessage('');
            return;
        }

        const emailRE = new RegExp(/[^@]+@[^@]+\.[^@]+/);
        const isValidFormat = emailRE.test(email);

        if (email.length > 0 && isValidFormat) {
            checkEmailAvailability(email);
        } else {
            setEmailValid(false);
            setMessage('');
        }

        return () => checkEmailAvailability.cancel();
    },[email, mode, checkEmailAvailability]);

    // shared submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            mode === 'signup' ? await handleSignUp() : await handleLogin(e);
        } catch (err) {
            console.error('Auth submission error:', err);
        } finally {
            setLoading(false);
        }
    };

    // disables submit button
    const isFormIncomplete = email?.length < 1 || password?.length < 1;
    const isSignupValid = emailValid && password?.length > 0;

    const isSubmitDisabled = loading || isFormIncomplete || (mode === 'signup' && !isSignupValid);

    return (
        <AnimatePresence mode='wait'>
            <motion.form 
                className={styles.form} 
                onSubmit={handleSubmit}
                transition={transition}
                variants={variants}
                initial='hide'
                animate='show'
                exit='hide'
            >
                {!setUp && (
                    <>
                    <AnimatePresence mode='wait' initial={false}>
                        {mode === 'login' ?
                            <motion.h2 
                                key='login-header'
                                className={styles.header}
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                            >Login</motion.h2>
                        :
                            <motion.h2
                                key='signup-header'
                                className={styles.header}
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                            >Sign Up</motion.h2>
                        }
                    </AnimatePresence>
                    <AnimatePresence mode='wait' initial={false}>
                        {mode === 'login' ? 
                            <motion.div
                                key='login-subheader'
                                className={styles.subHeader}
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                            >
                                Need an account? <span onClick={() => toggleModeRedirect('signup')}>SIGN UP</span>
                            </motion.div>
                        :
                            <motion.div
                                key='signup-subheader'
                                className={styles.subHeader}
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                            >
                                Already a user? <span onClick={() => toggleModeRedirect('login')}>LOGIN</span>
                            </motion.div>
                        }
                    </AnimatePresence>
                    <AnimatePresence mode='wait' initial={false}>
                        {inviteData && (
                            <motion.div
                                key='invite-info'
                                className={styles.inviteInfo}
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                            >
                                {mode === 'signup' 
                                    ? "You're joining an organization on LiquidQR" 
                                    : "You're invited to join an organization on LiquidQR. Please log in to accept."}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    </>
                                )}
                <div className={styles.inputGroup}>
                    <input
                        type='email'
                        id='auth-email'
                        value={email}
                        className={styles.input}
                        onChange={handleInputChange(setEmail)}
                        required
                        autoComplete={mode === 'login' ? 'email' : 'off'}
                    />
                    <label htmlFor='auth-email' className={styles.label}>Email</label>
                    <AnimatePresence initial={false}>
                        {(mode === 'signup' && message?.length > 0) && 
                            <motion.div 
                                key='message'
                                className={styles.message}
                                transition={transition}
                                variants={variants}
                                initial='hideMessage'
                                animate='showMessage'
                                exit='hideMessage'
                            >{message}</motion.div>
                        }
                    </AnimatePresence>
                    <AnimatePresence intial={false}>
                        {(mode === 'signup' && email?.length > 0) &&
                            <motion.div
                                key='signup-icon'
                                className={styles.icon}
                                transition={transition}
                                variants={variants}
                                initial='hideIcon'
                                animate='showIcon'
                                exit='hideIcon'
                            >
                                <i className={`far ${emailValid ? 'fa-check-circle' : 'fa-times-circle'}`} style={{color: `${emailValid ? 'green' : 'red'}`}} />
                            </motion.div>
                        }
                    </AnimatePresence>
                </div>
                <div className={styles.inputGroup}>
                    <input
                        type={inputType}
                        className={styles.input}
                        id='auth-password'
                        value={password}
                        onChange={handleInputChange(setPassword)}
                        autoComplete={mode === 'login' ? 'current-password' : 'off'}
                        required
                    />
                    <label htmlFor='auth-password' className={styles.label}>Password</label>
                    <button 
                        type='button' 
                        className={styles.hidePassword} 
                        onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')} 
                        disabled={password?.length < 1}
                        aria-label={inputType === 'password' ? 'Show Password' : 'Hide Password'}
                    >
                        <i className={`fas ${inputType === 'password' ? 'fa-eye' : 'fa-eye-slash'}`} />
                    </button>
                    <AnimatePresence mode='wait' initial={false}>
                        {mode === 'login' ? 
                            <motion.a
                                key='forgot-password'
                                className={styles.forgotPassword}
                                transition={transition}
                                variants={variants}
                                initial='hideMessage'
                                animate='showMessage'
                                exit='hideMessage'
                                href={'/reset'}
                            >Forgot Password?</motion.a>
                        :
                            <motion.div
                                key='password-strength'
                                style={{width: '100%'}}
                                transition={transition}
                                variants={variants}
                                initial='hideMessage'
                                animate='showMessage'
                                exit='hideMessage'
                            >
                                <PasswordStrength password={password} />
                            </motion.div>
                        }
                    </AnimatePresence>
                </div>
                {gen_id && <GeneratorQRDisplay gen_id={gen_id} state={signupState} setState={setSignupState} />}
                <div className={styles.inputGroup} style={{flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                    <button 
                        type='submit'
                        className={styles.btn} 
                        disabled={isSubmitDisabled}
                    >
                        {loading ? <i className="fa-solid fa-spinner fa-spin-pulse" /> : 'Submit'}
                    </button>
                    {loginError && <p className='text-center' style={{color: 'red'}}>Email or password is incorrect.</p>}
                </div>
            </motion.form>
        </AnimatePresence>
    );
};